import { DocumentFiles } from "domain/entities/document-files";
import { DocumentFilesInputFilters } from "domain/models/document-files-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import { useDeleteDocumentFilesMutation, useDocumentFilesByFilter, useUpdateDocumentFilesMutation } from "presentation/hooks/use-query-document-files";
import { useDocumentFilesStore } from "presentation/stores/use-document-files-store";
import { useDocumentFilesSelectedStore } from "presentation/stores/use-document-files-selected-store";
import { useFilterDocumentFilesStore } from "presentation/stores/use-filter-document-files-store";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import HeaderDocumentFilesGrid from "./HeaderDocumentFilesGrid";
import { Dialog } from "primereact/dialog";
import DocumentFilesForm from "../DocumentFilesForm";
import { DocumentFilesType } from "domain/entities/document-files";
import TypeDocumentFilesDropdown from "./TypeDocumentFilesDropdown";
import { httpClient } from "infra/axios/axios-api";
import { InputNumber } from "primereact/inputnumber";
import { Tooltip } from "primereact/tooltip";
import { Checkbox } from "primereact/checkbox";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useRoleByFilter } from "presentation/hooks/use-query-role";
import { RoleInputFilter } from "domain/models/role-input-filter";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { EllipsisCell } from "presentation/components/Shared/EllipsisCellComponent";

const DocumentFilesGrid: React.FC<{mode: "user" | "admin", type: "regulations" | "tutorials" }> = ({mode, type}) => {
  const navigate = useNavigate();
  const { filters, setFilters } = useFilterDocumentFilesStore();
  const { documentfile, setDocumentFile } = useDocumentFilesStore();
  const { selectedDocumentFile, setSelectedDocumentFile } = useDocumentFilesSelectedStore();
  const toast = useRef<Toast>(null);
  const [formMode, setFormMode] = useState<"create" | "edit" | "view">("create");
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<{url: string, type: string, name: string} | null>(null);
  const deleteMutation = useDeleteDocumentFilesMutation();
  const documentType = type === "regulations" ? DocumentFilesType.REGULATION : DocumentFilesType.TUTORIAL;
  const [lazyParams, setLazyParams] = useState({
    sortBy: "order",
    sortOrder: "asc",
    limit: 50,
    page: 1,
    filters: {},
  });
  const {
    data: documentfiles,
    isLoading,
    isError,
    error,
    refetch,
  } = useDocumentFilesByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
    type: documentType
  } as DocumentFilesInputFilters);

  const { userPermissions } = useAuthStore();

  const { data: rolesData } = useRoleByFilter({
    limit: 100,
  } as RoleInputFilter);
  const userRoleNames = useMemo(() => {
  if (!rolesData?.data || !userPermissions) {
    return [];
  }

  return rolesData.data
    .filter((role) =>
      userPermissions.some((permission) => permission.roleId === role.id)
    )
    .map((role) => role.name);
}, [rolesData, userPermissions]);

 const documentsToShow =  mode === "admin"
    ? documentfiles?.data ?? []
    : (documentfiles?.data ?? []).filter((doc) => {
        if (!doc.isPrivate) {
          return true;
        }

        return doc.allowedRoles?.some((role: string) =>
          userRoleNames.includes(role)
        );
      })

 const {
    data: categoriesResponse,
  } = useParameterByFilter({
    page: 1,
    limit: 500,
    type: "categoryRoleOptions",
  } as BaseInputFilter);

  const categoryRoleOptions =
    categoriesResponse?.data?.map(
      (item: any) => ({
        label: item.value,
        value: item.value,
      })
    ) || [];
    

const parameters = categoriesResponse?.data ?? [];

const roleOptions = parameters
  .filter((item) => item.value !== "Geral")
  .map((item) => item.value);
  
  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);
console.log(filters);
  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

const handleRowReorder = async (e: any) => {
  const pageStart = (lazyParams.page - 1) * lazyParams.limit;

  const movedDocument = documentsToShow[e.dragIndex];

  const newOrder = pageStart + e.dropIndex + 1;

  try {
    await httpClient.patch(
      `/documentFiles/${movedDocument.id}/order`,
      {
        order: newOrder,
      }
    );

    await refetch();
  } catch (error) {
    console.error(error);
    await refetch(); 
  }
};

 const deleteOldFile = async (urlPath: string) => {
  try {
    await httpClient.delete(`/assets/${urlPath}`);
  } catch (err) {
    console.error(err);
  }
};

const handleDelete = async (rowData: DocumentFiles) => {
  try {
    if (!rowData.id) {
      throw new Error("ID inválido");
    }

    await deleteMutation.mutateAsync(rowData.id);
    await deleteOldFile(rowData.urlPath);
    setDocumentFile(null);
    await refetch();

    toast.current?.show({
      severity: "success",
      summary: "Sucesso",
      detail: "Documento eliminado com sucesso.",
      life: 3000,
    });
  } catch (error: any) {
    toast.current?.show({
      severity: "error",
      summary: "Erro",
      detail: error.message || "Erro ao eliminar documento.",
      life: 3000,
    });
  }
};

  const confirmAction = (message: string, acceptCallback: () => void) => {
        confirmDialog({
          message: message,
          header: "Confirmação",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Sim",
          rejectLabel: "Não",
          accept: acceptCallback,
        });
      };

  const showActionOptions = (rowData: DocumentFiles) => {
    return (
      <div className="spacing-icons">
        {mode === "admin" ? (
          <><i
            className="pi pi-trash cursor-pointer"
            onClick={() => {
              confirmAction(`Deseja apagar este registo?`, () => handleDelete(rowData));
            } } /><i
              className="pi pi-pencil cursor-pointer"
              onClick={() => {
                setSelectedDocumentFile(rowData);
                setFormMode("edit");
                setShowModal(true);
              } }/><i
              className="pi pi-eye cursor-pointer"
              onClick={() => {
                setSelectedDocumentFile(rowData);
                setFormMode("view");
                setShowModal(true)
              }}
        /></>
             ) : (
                    <Button
                    icon="pi pi-eye"
                    tooltip="Pré-visualizar/Descarregar"
                    tooltipOptions={{ position: 'top' }}
                    onClick={async () => {
                      try {
                        const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(rowData.urlPath);
                        const isPdf = /\.pdf$/i.test(rowData.urlPath);
                        const isVideo = /\.(mp4|webm|ogg)$/i.test(rowData.urlPath);
                    
                        const endpoint = (isImage || isPdf || isVideo) ? `/assets/inline/${rowData.urlPath}` : `/assets/${rowData.urlPath}`;
          
                        const response = await httpClient.get(endpoint);
                        
                        let fileType = "other";
                        if (isImage) fileType = "image";
                        else if (isPdf) fileType = "pdf";
                        else if (isVideo) fileType = "video";
                        
                        if (fileType !== "other") {
                           setPreviewData({ url: response.data.url, type: fileType, name: rowData.title });
                           setShowPreviewModal(true);
                        } else {
                           // Fallback to direct open/download for unknown types (docx, xlsx, etc)
                           window.open(response.data.url, "_blank");
                        }
                      } catch (error) {
                        console.error("Erro ao obter URL do ficheiro:", error);
                      }
                    }}
                    style={{
                    backgroundColor: "#2196f3",
                    color: "white",
                    borderRadius: "8px"}}/>)}                 
      </div>
    );
  };

  const dialogHeader =
    formMode === "edit"
      ? "Editar Registo"
      : "Ver Registo";

  return (
    <div>
      <Dialog
        header={dialogHeader}
        visible={showModal}
        onHide={() => setShowModal(false)}
        style={{
          width: "70vw",
          maxWidth: "600px"
        }}
      >
        <DocumentFilesForm 
        mode={formMode} 
        initialData={selectedDocumentFile} 
        onSuccess={() => {
        toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: formMode === "edit" ? "Documento atualizado com sucesso." : "Documento criado com sucesso.",
        life: 3000,});
        setShowModal(false);
        setSelectedDocumentFile(null);
        refetch();
        }}
        type={documentType} />
      </Dialog>

      <Dialog
        header={previewData?.name || "Pré-visualização"}
        visible={showPreviewModal}
        onHide={() => {
          setShowPreviewModal(false);
          setPreviewData(null);
        }}
        maximizable
        style={{ width: "80vw", height: "90vh" }}
        contentStyle={{ padding: "1.5rem", overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        {previewData?.type === "image" && (
          <img src={previewData.url} alt={previewData.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", border: "1px solid #dee2e6", borderRadius: "4px", padding: "4px", backgroundColor: "#fff" }} />
        )}
        {previewData?.type === "pdf" && (
          <iframe src={previewData.url} width="100%" height="100%" style={{ border: "none", width: "100%", height: "100%" }} title={previewData.name}></iframe>
        )}
        {previewData?.type === "video" && (
          <video src={previewData.url} controls autoPlay style={{ maxWidth: "100%", maxHeight: "100%" }} />
        )}
      </Dialog>
      <Tooltip target="[data-pr-tooltip]" />
      <DataTable
        value={documentsToShow}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={documentfiles?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderDocumentFilesGrid mode={mode} type={type}/>}
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["order","title", "description", "category", "isPrivate", "allowedRoles"]}
        reorderableRows={mode === "admin"}
        onRowReorder={mode === "admin" ? handleRowReorder : undefined}
>
    {mode === "admin" && (
        <Column
            rowReorder
            style={{ width: "3rem" }}
        />
    )}
    {mode === "admin" && (
          <Column
          field="order"
          header="Ordem"
          sortable
          filter
          filterElement={(options) => (
            <InputNumber
            value={options.value}
            min={0}
            onValueChange={(e) => options.filterApplyCallback(e.value)}
            useGrouping={false}
            className="w-full"
            placeholder="Ordem"
            />
          )}
          showFilterMatchModes={false}
        /> )}
           <Column
          field="title"
          header="Título"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
        field="description"
        header="Descrição"
        sortable
        filter
        style={{ width: "250px" }}
        body={(rowData) => (
          rowData.description === "" ? "-" : <EllipsisCell text={rowData.description} />
        )}
        showFilterMatchModes={false}
        />
           <Column
          field="category"
          header="Categoria"
          sortable
          filter
          filterElement={(options) => (
              <TypeDocumentFilesDropdown options={options} inputType="category" />
            )}
          showFilterMatchModes={false}
        />
        { mode === "admin" && (
          <Column
          field="isPrivate"
          header="Visibilidade"
          sortable
          filter
          filterElement={(options) => (
              <TypeDocumentFilesDropdown options={options} inputType="isPrivate" />
            )}
          body={(rowData) => (rowData.isPrivate === true ? "Privado" : "Público")}
          showFilterMatchModes={false}
        /> )}
           <Column
          field="allowedRoles"
          header="Acesso Autorizado"
          filter
          filterElement={(options) => {
    const selectedRoles = options.value || [];

    return (
      <div className="flex flex-column gap-2 p-2">
        {roleOptions.map((role) => (
          <div
            key={role}
            className="flex align-items-center gap-2"
          >
            <Checkbox
              inputId={`filter-${role}`}
              checked={selectedRoles.includes(role)}
              onChange={(e) => {
                const newRoles = e.checked
                  ? [...selectedRoles, role]
                  : selectedRoles.filter((r: string) => r !== role);

                options.filterApplyCallback(newRoles);
              }}
            />
            <label htmlFor={`filter-${role}`}>
              {role}
            </label>
          </div>
        ))}
      </div>
    );
  }}
         body={(rowData) => {
          const roles: string[] = rowData.allowedRoles || [];

          if (roles.length === 0) {
            return "-";
          }

    const maxVisible = 2;
    const visibleRoles = roles.slice(0, maxVisible);
    const remaining = roles.length - maxVisible;

    return (
    <div className="flex flex-wrap gap-2" data-pr-tooltip={roles.join(", ")} data-pr-position="top">
      {visibleRoles.map((role) => (
        <div
          key={role}
          className="px-2 py-1 text-sm shadow-1"
          style={{
            backgroundColor: "#2196f3",
            color: "white",
            fontSize: "0.75rem",
            borderRadius: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {role}
        </div>
      ))}

      {remaining > 0 && (
        <div
          className="px-2 py-1 text-sm shadow-1"
          style={{
            backgroundColor: "#6c757d",
            color: "white",
            fontSize: "0.75rem",
            borderRadius: "12px",
            whiteSpace: "nowrap",
          }}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
}}
          showFilterMatchModes={false}
        />
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
}

export default DocumentFilesGrid;